import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Download, Trash2, Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function StockReceipt() {
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [itemSearchOpen, setItemSearchOpen] = useState(false);
  const [formData, setFormData] = useState({
    supplier_id: '',
    invoice_number: '',
    date: new Date().toISOString().split('T')[0],
    item_id: '',
    qty: '',
    unit: '',
    price: ''
  });

  useEffect(() => {
    fetchItems();
    fetchSuppliers();
    fetchReceipts();
  }, []);

  const fetchItems = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/items`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to fetch items');
    }
  };

  const fetchSuppliers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Failed to fetch suppliers');
    }
  };

  const fetchReceipts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/stock-receipts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReceipts(response.data);
    } catch (error) {
      toast.error('Failed to fetch receipts');
    }
  };

  const handleItemSelect = (itemId) => {
    const selectedItem = items.find(i => i.id === itemId);
    if (selectedItem) {
      setFormData({ ...formData, item_id: itemId, unit: selectedItem.unit });
    }
    setItemSearchOpen(false);
  };

  const getSelectedItemName = () => {
    const selectedItem = items.find(i => i.id === formData.item_id);
    return selectedItem ? `${selectedItem.name} (${selectedItem.category})` : 'Search and select an item...';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        date: new Date(formData.date).toISOString(),
        qty: parseFloat(formData.qty),
        price: parseFloat(formData.price)
      };

      await axios.post(`${BACKEND_URL}/api/stock-receipts`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Stock receipt created successfully');
      setFormData({
        supplier_id: '',
        invoice_number: '',
        date: new Date().toISOString().split('T')[0],
        item_id: '',
        qty: '',
        unit: '',
        price: ''
      });
      fetchReceipts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create receipt');
    }
  };

  const handleDelete = async (receiptId) => {
    if (!window.confirm('Are you sure you want to delete this stock receipt?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/stock-receipts/${receiptId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Stock receipt deleted successfully');
      fetchReceipts();
    } catch (error) {
      toast.error('Failed to delete stock receipt');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight font-heading" data-testid="stock-receipt-title">Stock Receipt</h1>
        <p className="text-slate-600 mt-2">Record incoming inventory items</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-xl font-semibold">New Receipt</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="receipt-form">
              <div className="space-y-2">
                <Label htmlFor="supplier">Supplier</Label>
                <Select value={formData.supplier_id} onValueChange={(value) => setFormData({ ...formData, supplier_id: value })} required>
                  <SelectTrigger data-testid="receipt-supplier-select">
                    <SelectValue placeholder="Select a supplier" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="invoice_number">Invoice Number</Label>
                <Input
                  id="invoice_number"
                  data-testid="receipt-invoice-input"
                  value={formData.invoice_number}
                  onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  data-testid="receipt-date-input"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="item">Item</Label>
                <Popover open={itemSearchOpen} onOpenChange={setItemSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={itemSearchOpen}
                      className="w-full justify-between font-normal"
                      data-testid="receipt-item-select"
                    >
                      <span className={formData.item_id ? "text-slate-900" : "text-slate-500"}>
                        {getSelectedItemName()}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search items by name or category..." />
                      <CommandList>
                        <CommandEmpty>No item found.</CommandEmpty>
                        <CommandGroup>
                          {items.map((item) => (
                            <CommandItem
                              key={item.id}
                              value={`${item.name} ${item.category}`}
                              onSelect={() => handleItemSelect(item.id)}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  formData.item_id === item.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <div className="flex flex-col">
                                <span className="font-medium">{item.name}</span>
                                <span className="text-xs text-slate-500">{item.category} • {item.unit} • ₹{item.unit_price?.toFixed(2) || '0.00'}</span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="qty">Quantity</Label>
                  <Input
                    id="qty"
                    data-testid="receipt-qty-input"
                    type="number"
                    step="0.01"
                    value={formData.qty}
                    onChange={(e) => setFormData({ ...formData, qty: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="unit">Unit</Label>
                  <Input
                    id="unit"
                    data-testid="receipt-unit-input"
                    value={formData.unit}
                    readOnly
                    className="bg-slate-50"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="price">Price (₹)</Label>
                <Input
                  id="price"
                  data-testid="receipt-price-input"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                />
              </div>

              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="submit-receipt-button">
                <Download className="w-4 h-4 mr-2" />
                Record Receipt
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Recent Receipts */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-xl font-semibold">Recent Receipts</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-3 max-h-[600px] overflow-y-auto" data-testid="recent-receipts-list">
              {receipts.slice(0, 10).map((receipt) => (
                <div key={receipt.id} className="p-4 bg-slate-50 border border-slate-200 rounded-lg" data-testid={`receipt-${receipt.id}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">{receipt.item_name}</p>
                      <p className="text-sm text-slate-600">{receipt.supplier}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded">
                        +{receipt.qty} {receipt.unit}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(receipt.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-receipt-${receipt.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                    <div>Invoice: {receipt.invoice_number}</div>
                    <div>Price: ₹{receipt.price}</div>
                    <div className="col-span-2">Date: {new Date(receipt.date).toLocaleDateString()}</div>
                  </div>
                </div>
              ))}
              {receipts.length === 0 && (
                <div className="text-center py-8 text-slate-600">
                  No receipts recorded yet
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default StockReceipt;
